<template>
    <v-container class="py-4" style="max-width: 960px">
        <div class="d-flex align-center ga-2 mb-4">
            <v-icon icon="mdi-book-open-variant" size="28"/>
            <h2 class="text-h5 font-weight-bold mb-0">Mileage Log</h2>
        </div>

        <!-- Premium gate: covers deep links, not just the sidebar item. -->
        <v-card v-if="premiumChecked && !isPremium" variant="outlined" rounded="lg" class="pa-6 text-center">
            <v-icon icon="mdi-lock-outline" size="40" class="mb-2 text-medium-emphasis"/>
            <div class="text-h6 mb-1">Mileage Log is a premium feature</div>
            <div class="text-medium-emphasis mb-4">
                Upgrade your circle plan to classify trips and export an IRS mileage report.
            </div>
            <v-btn v-if="authStore.isCircleOwner" color="primary" @click="goToPlans">See plans</v-btn>
            <div v-else class="text-caption">Ask your circle owner to upgrade the plan.</div>
        </v-card>

        <template v-else-if="isPremium">
        <!-- Settings: enable classification + business names -->
        <v-card variant="outlined" rounded="lg" class="mb-4">
            <v-card-text>
                <v-switch v-model="enabled" color="primary" hide-details density="comfortable"
                          label="Enable trip classification"
                          :loading="settingsLoading"/>
                <div class="text-caption text-medium-emphasis mb-2">
                    Tag each drive as business or personal to build an IRS mileage log.
                </div>

                <template v-if="enabled">
                    <div class="text-subtitle-2 font-weight-medium mt-2">Your businesses</div>
                    <div class="text-caption text-medium-emphasis mb-3">
                        Name up to four. Business 1 is your default - trips you have not
                        classified count against it.
                    </div>
                    <v-row dense>
                        <v-col v-for="i in 4" :key="i" cols="12" sm="6">
                            <v-text-field v-model="businessNames[i - 1]" density="compact" variant="outlined"
                                          :label="i === 1 ? 'Business 1 (primary)' : `Business ${i}`"
                                          :placeholder="i === 1 ? 'e.g. your name or company' : 'Optional'"
                                          maxlength="100" hide-details/>
                        </v-col>
                    </v-row>
                </template>

                <div class="d-flex justify-end mt-4">
                    <v-btn color="primary" :loading="saving" @click="saveSettings">Save</v-btn>
                </div>
            </v-card-text>
        </v-card>

        <!-- Report: pick year + business, view summary, download PDF -->
        <v-card variant="outlined" rounded="lg">
            <v-card-title class="text-subtitle-1 font-weight-bold">Year-end report</v-card-title>
            <v-card-text>
                <v-row dense align="center">
                    <v-col cols="6" sm="3">
                        <v-select v-model="year" :items="yearOptions" label="Year" density="compact"
                                  variant="outlined" hide-details/>
                    </v-col>
                    <v-col cols="6" sm="4">
                        <v-select v-model="businessFilter" :items="businessFilterOptions"
                                  item-title="label" item-value="value" label="Business" density="compact"
                                  variant="outlined" hide-details/>
                    </v-col>
                    <v-col cols="12" sm="5" class="d-flex ga-2">
                        <v-btn color="primary" variant="tonal" :loading="reportLoading" @click="loadReport">
                            View
                        </v-btn>
                        <v-btn color="primary" prepend-icon="mdi-file-pdf-box" :loading="pdfLoading"
                               :disabled="!report" @click="downloadPdf">
                            Download PDF
                        </v-btn>
                    </v-col>
                </v-row>

                <div v-if="report" class="mt-4">
                    <!-- Totals -->
                    <v-row dense class="mb-2">
                        <v-col cols="12" sm="4">
                            <v-card variant="tonal" color="primary" rounded="lg">
                                <v-card-text class="py-3">
                                    <div class="text-caption">Business miles</div>
                                    <div class="text-h6 font-weight-bold">{{ fmt(report.totals.business_miles) }}</div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-card variant="tonal" rounded="lg">
                                <v-card-text class="py-3">
                                    <div class="text-caption">Personal miles</div>
                                    <div class="text-h6 font-weight-bold">{{ fmt(report.totals.personal_miles) }}</div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col cols="12" sm="4">
                            <v-card variant="tonal" color="success" rounded="lg">
                                <v-card-text class="py-3">
                                    <div class="text-caption">Deduction</div>
                                    <div class="text-h6 font-weight-bold">${{ fmt(report.totals.deduction, 2) }}</div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <!-- Review disclosures: don't present the report as audit-ready
                         while it contains unreviewed assumptions. -->
                    <v-alert v-if="reviewNotes.length" type="warning" variant="tonal"
                             density="compact" class="mb-2" icon="mdi-alert-outline">
                        <div class="text-subtitle-2">Review before filing</div>
                        <ul class="ms-4">
                            <li v-for="(n, i) in reviewNotes" :key="i">{{ n }}</li>
                        </ul>
                    </v-alert>

                    <!-- Monthly breakdown -->
                    <v-table density="compact" class="mt-2">
                        <thead>
                        <tr>
                            <th class="text-left">Month</th>
                            <th class="text-right">Trips</th>
                            <th class="text-right">Business mi</th>
                            <th class="text-right">Personal mi</th>
                            <th class="text-right">Deduction</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="m in report.months" :key="m.month">
                            <td>{{ m.label }}</td>
                            <td class="text-right">{{ m.trips }}</td>
                            <td class="text-right">{{ fmt(m.business_miles) }}</td>
                            <td class="text-right">{{ fmt(m.personal_miles) }}</td>
                            <td class="text-right">${{ fmt(m.deduction, 2) }}</td>
                        </tr>
                        <tr v-if="!report.months.length">
                            <td colspan="5" class="text-center text-medium-emphasis py-4">
                                No trips recorded for this year.
                            </td>
                        </tr>
                        </tbody>
                    </v-table>
                    <div class="text-caption text-medium-emphasis mt-2">
                        Standard IRS mileage rate applied per trip date. Commute miles between
                        home and a regular workplace are not deductible - classify those as Personal.
                    </div>
                </div>
            </v-card-text>
        </v-card>
        </template>
    </v-container>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {apiErrorMessage} from "@/composables/useApiError";

const authStore = useAuthStore();
const {showSnackbar} = useSnackbarStore();

// api #1330 enforces the Mileage Log paywall on the server, so all three of
// these can now come back 402. That is a permanent refusal, and every message
// below words a failure as a transient one - so name the plan when it is the
// plan, and only then.
const refusalOr = (error, fallback) =>
    apiErrorMessage(error, fallback, {canUpgrade: authStore.isCircleOwner}).message;
const router = useRouter();

// Premium (active paid circle plan) gates the whole page - covers deep links,
// not just the sidebar item. Sourced from entitlements.is_premium.
const isPremium = ref(false);
const premiumChecked = ref(false);
const goToPlans = () => router.push({name: "plans"});
const checkPremium = async () => {
    try {
        const {data} = await send("get", "go-subscription");
        isPremium.value = data?.data?.entitlements?.is_premium === true;
    } catch (e) {
        // Leave locked on failure.
    } finally {
        premiumChecked.value = true;
    }
};

const enabled = ref(false);
const businessNames = ref(["", "", "", ""]);
const settingsLoading = ref(true);
const saving = ref(false);

const currentYear = new Date().getFullYear();
const yearOptions = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
const year = ref(currentYear);
const businessFilter = ref("all");
const report = ref(null);
const reportLoading = ref(false);
const pdfLoading = ref(false);

const businessFilterOptions = computed(() => {
    const opts = [{label: "All businesses", value: "all"}, {label: "Personal", value: "personal"}];
    businessNames.value.forEach((name, i) => {
        const n = (name || "").trim();
        if (n) opts.push({label: n, value: String(i + 1)});
    });
    return opts;
});

const fmt = (v, dp = 1) => Number(v ?? 0).toFixed(dp);

// Human-readable review flags surfaced from the report payload.
const reviewNotes = computed(() => {
    const r = report.value;
    if (!r) return [];
    const notes = [];
    if ((r.unclassified_assumed_business ?? 0) > 0) {
        notes.push(`${r.unclassified_assumed_business} trip(s) were not classified and counted as business by default - confirm each is truly business (mark personal/commute trips as Personal).`);
    }
    if ((r.missing_purpose ?? 0) > 0) {
        notes.push(`${r.missing_purpose} business trip(s) have no purpose noted; the IRS expects a business purpose per trip.`);
    }
    if (r.rate_estimated) {
        notes.push(`The IRS rate for ${r.year} is not yet published; the deduction uses an estimated rate.`);
    }
    return notes;
});

const loadSettings = async () => {
    settingsLoading.value = true;
    try {
        const {data} = await send("get", "/mileage/settings");
        const d = data?.data;
        if (d) {
            enabled.value = d.enabled === true;
            businessNames.value = [d.business_1 || "", d.business_2 || "",
                d.business_3 || "", d.business_4 || ""];
        }
    } catch (e) {
        showSnackbar("Could not load mileage settings", "error");
    } finally {
        settingsLoading.value = false;
    }
};

const saveSettings = async () => {
    const nz = (s) => {
        const v = (s || "").trim();
        return v === "" ? null : v;
    };
    // A named primary business is required when the log is on - otherwise
    // unclassified trips default to a nameless "Business 1".
    if (enabled.value && nz(businessNames.value[0]) === null) {
        showSnackbar("Name your primary business to turn this on", "error");
        return;
    }
    saving.value = true;
    try {
        await send("put", "/mileage/settings", {
            enabled: enabled.value,
            business_1: nz(businessNames.value[0]),
            business_2: nz(businessNames.value[1]),
            business_3: nz(businessNames.value[2]),
            business_4: nz(businessNames.value[3]),
        });
        showSnackbar("Mileage settings saved");
    } catch (e) {
        showSnackbar(refusalOr(e, "Could not save settings"), "error");
    } finally {
        saving.value = false;
    }
};

const loadReport = async () => {
    reportLoading.value = true;
    try {
        const {data} = await send("get", "/mileage/report",
            {year: year.value, business: businessFilter.value});
        const d = data?.data;
        // Guarantee the shape the template dereferences, even on a partial payload.
        report.value = d
            ? {months: [], totals: {business_miles: 0, personal_miles: 0, deduction: 0}, ...d}
            : null;
    } catch (e) {
        showSnackbar(refusalOr(e, "Could not load report"), "error");
    } finally {
        reportLoading.value = false;
    }
};

const downloadPdf = async () => {
    pdfLoading.value = true;
    try {
        const response = await send("get", "/mileage/report/pdf",
            {year: year.value, business: businessFilter.value}, {responseType: "blob"});
        const blob = new Blob([response.data], {type: "application/pdf"});
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mileage-${year.value}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    } catch (e) {
        showSnackbar(refusalOr(e, "Could not download PDF"), "error");
    } finally {
        pdfLoading.value = false;
    }
};

onMounted(async () => {
    await checkPremium();
    if (isPremium.value) {
        loadSettings();
    } else {
        settingsLoading.value = false;
    }
});
</script>
